// ── Mobile Tutorial ──
// First-time mobile player onboarding overlay.
// Shows step-by-step instructions highlighting each control area
// with a spotlight effect and text descriptions.

import { isMobile } from '../core/device';

/** localStorage key for tracking whether the tutorial has been seen. */
export const TUTORIAL_STORAGE_KEY = 'cp-mobile-tutorial-seen';
export const TUTORIAL_DISMISS_STORAGE_KEY = 'mobile_tutorial_dismissed';

// ── Types ──

export interface TutorialStep {
  /** Step title shown prominently. */
  title: string;
  /** Description text explaining what to do. */
  description: string;
  /**
   * Spotlight area on screen (percentage-based).
   * Used to position the highlight circle.
   */
  spotlightArea: {
    x: number;   // % from left
    y: number;   // % from top
    radius: number; // px radius of spotlight
  };
  /** Optional: if true, this step is conditional (e.g., gyro). */
  conditional?: boolean;
  /**
   * Optional: DOM id of a HUD button (e.g., 'btn-fire') whose live
   * bounding rect should override the hardcoded spotlightArea at render
   * time. Lets the spotlight follow buttons that the user has moved via
   * HudLayoutEditor. Falls back to spotlightArea when the element is
   * absent.
   */
  buttonId?: string;
}

// ── Tutorial Steps ──

export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    title: 'Move',
    description: 'Use the left joystick to move your character. Drag in any direction to walk or run.',
    spotlightArea: { x: 15, y: 65, radius: 80 },
  },
  {
    title: 'Look Around',
    description: 'Swipe on the right side of the screen to look around and aim.',
    spotlightArea: { x: 70, y: 50, radius: 100 },
  },
  {
    title: 'Fire & ADS',
    description: 'Tap the FIRE button to shoot. Use the ADS button to aim down sights for better accuracy.',
    spotlightArea: { x: 88, y: 55, radius: 70 },
    buttonId: 'btn-fire',
  },
  {
    title: 'Reload & Switch',
    description: 'Tap R to reload your weapon. Use the WPN button to switch weapons.',
    spotlightArea: { x: 90, y: 40, radius: 60 },
    buttonId: 'btn-reload',
  },
  {
    title: 'Gyroscope Aiming',
    description: 'Tilt your device to fine-tune your aim. Enable gyroscope in Settings for precision aiming.',
    spotlightArea: { x: 50, y: 50, radius: 90 },
    conditional: true,
  },
  {
    title: 'Ready!',
    description: 'You\'re all set! Jump into a match and show them what you\'ve got. You can replay this tutorial from Settings.',
    spotlightArea: { x: 50, y: 50, radius: 120 },
  },
];

function detectGyroscopeSupport(): boolean {
  if (typeof window === 'undefined') return true;
  return typeof DeviceMotionEvent !== 'undefined';
}

function isLandscapeOrientation(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia("(orientation: landscape)").matches;
}

// ── Tutorial Class ──

export class MobileTutorial {
  private active = false;
  private currentStepIndex = 0;
  private overlay: HTMLDivElement | null = null;
  private contentEl: HTMLDivElement | null = null;
  private dontShowAgainInput: HTMLInputElement | null = null;
  private seen: boolean;
  private readonly hasGyroscope: boolean;

  constructor(hasGyroscope = detectGyroscopeSupport()) {
    this.hasGyroscope = hasGyroscope;
    this.seen = this.readFlag();
  }

  /** Whether the tutorial should be shown (not previously seen/skipped). */
  shouldShow(): boolean {
    return !this.seen && isLandscapeOrientation();
  }

  /** Get the current step index. */
  getCurrentStep(): number {
    return this.currentStepIndex;
  }

  /** Whether the tutorial is currently active. */
  isActive(): boolean {
    return this.active;
  }

  /** Start the tutorial overlay at step 0. */
  start(): void {
    if (this.active) return;
    if (!isLandscapeOrientation()) return;
    const firstStep = this.findStepIndex(0, 1);
    if (firstStep === -1) return;
    this.active = true;
    this.currentStepIndex = firstStep;
    this.createOverlay();
    this.renderStep();
  }

  /** Advance to the next step. If past the end, complete the tutorial. */
  nextStep(): void {
    if (!this.active) return;
    const nextStep = this.findStepIndex(this.currentStepIndex + 1, 1);
    if (nextStep === -1) {
      this.complete();
      return;
    }
    this.currentStepIndex = nextStep;
    this.renderStep();
  }

  /** Go back to the previous step. */
  previousStep(): void {
    if (!this.active) return;
    const previousStep = this.findStepIndex(this.currentStepIndex - 1, -1);
    if (previousStep !== -1) {
      this.currentStepIndex = previousStep;
      this.renderStep();
    }
  }

  /** Skip the entire tutorial — mark as seen and dismiss. */
  skip(): void {
    this.complete();
  }

  /** Clean up all DOM elements. */
  dispose(): void {
    this.active = false;
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    this.contentEl = null;
    this.dontShowAgainInput = null;
  }

  // ── Private ──

  /** Read the "seen" flag from localStorage. */
  private readFlag(): boolean {
    try {
      return localStorage.getItem(TUTORIAL_STORAGE_KEY) === 'true'
        || localStorage.getItem(TUTORIAL_DISMISS_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  /** Write the "seen" flag to localStorage. */
  private writeFlag(): void {
    try {
      localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    } catch {
      // localStorage unavailable
    }
    this.seen = true;
  }

  private writeDismissedFlag(): void {
    try {
      localStorage.setItem(TUTORIAL_DISMISS_STORAGE_KEY, '1');
    } catch {
      // localStorage unavailable
    }
  }

  private isStepAvailable(step: TutorialStep): boolean {
    return !step.conditional || this.hasGyroscope;
  }

  private getVisibleStepIndices(): number[] {
    const indices: number[] = [];
    for (let i = 0; i < TUTORIAL_STEPS.length; i++) {
      if (this.isStepAvailable(TUTORIAL_STEPS[i])) indices.push(i);
    }
    return indices;
  }

  /**
   * Resolve the live screen-space spotlight rect for a step, if the step
   * targets a known HUD button and that button is currently mounted in
   * the DOM with a non-zero bounding rect. Returns null otherwise so the
   * caller falls back to the hardcoded percentage coords.
   */
  private resolveSpotlightRect(
    step: TutorialStep,
  ): { cx: number; cy: number; radius: number } | null {
    if (!step.buttonId) return null;
    if (typeof document === 'undefined') return null;
    const getById = (document as { getElementById?: (id: string) => HTMLElement | null }).getElementById;
    if (typeof getById !== 'function') return null;
    const el = getById.call(document, step.buttonId);
    if (!el || typeof (el as HTMLElement).getBoundingClientRect !== 'function') return null;
    const rect = (el as HTMLElement).getBoundingClientRect();
    // Some buttons are mounted but hidden/zero-sized (e.g., advanced-mode
    // buttons in simple mode). Skip those and fall back.
    if (!rect || rect.width <= 0 || rect.height <= 0) return null;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    // Pad the spotlight slightly larger than the button so the ring is
    // visible around it rather than overlapping the glyph.
    const radius = Math.max(rect.width, rect.height) / 2 + 16;
    return { cx, cy, radius };
  }

  private findStepIndex(startIndex: number, direction: 1 | -1): number {
    for (let i = startIndex; i >= 0 && i < TUTORIAL_STEPS.length; i += direction) {
      if (this.isStepAvailable(TUTORIAL_STEPS[i])) return i;
    }
    return -1;
  }

  /** Mark the tutorial as complete: set flag, deactivate, remove overlay. */
  private complete(): void {
    if (this.dontShowAgainInput?.checked) {
      this.writeDismissedFlag();
    }
    this.writeFlag();
    this.active = false;
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    this.contentEl = null;
    this.dontShowAgainInput = null;
  }

  /** Create the full-screen overlay container. */
  private createOverlay(): void {
    this.overlay = document.createElement('div');
    this.overlay.id = 'mobile-tutorial-overlay';
    Object.assign(this.overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      zIndex: '3000',
      background: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'sans-serif',
      color: '#ffffff',
      touchAction: 'none',
    });

    // Content container
    this.contentEl = document.createElement('div');
    this.contentEl.id = 'tutorial-content';
    Object.assign(this.contentEl.style, {
      textAlign: 'center',
      maxWidth: '400px',
      padding: '24px',
    });
    this.overlay.appendChild(this.contentEl);

    // Bottom nav bar
    const navBar = document.createElement('div');
    Object.assign(navBar.style, {
      position: 'absolute',
      bottom: '24px',
      left: '0',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
      padding: '0 24px',
    });

    // Skip button
    const skipBtn = document.createElement('button');
    skipBtn.textContent = 'Skip';
    Object.assign(skipBtn.style, {
      position: 'absolute',
      top: '16px',
      right: '16px',
      padding: '8px 20px',
      background: 'rgba(255,255,255,0.1)',
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: '4px',
      color: '#ccc',
      fontFamily: 'sans-serif',
      fontSize: '14px',
      cursor: 'pointer',
      zIndex: '3001',
    });
    skipBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.skip();
    });
    skipBtn.addEventListener('click', () => this.skip());
    this.overlay.appendChild(skipBtn);

    // Previous button
    const prevBtn = document.createElement('button');
    prevBtn.textContent = 'Back';
    prevBtn.id = 'tutorial-prev';
    Object.assign(prevBtn.style, {
      padding: '10px 24px',
      background: 'rgba(255,255,255,0.1)',
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: '4px',
      color: '#fff',
      fontFamily: 'sans-serif',
      fontSize: '14px',
      cursor: 'pointer',
    });
    prevBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.previousStep();
    });
    prevBtn.addEventListener('click', () => this.previousStep());
    navBar.appendChild(prevBtn);

    // Next button
    const nextBtn = document.createElement('button');
    nextBtn.textContent = 'Next';
    nextBtn.id = 'tutorial-next';
    Object.assign(nextBtn.style, {
      padding: '10px 24px',
      background: 'rgba(0,180,255,0.5)',
      border: '1px solid rgba(0,180,255,0.7)',
      borderRadius: '4px',
      color: '#fff',
      fontFamily: 'sans-serif',
      fontSize: '14px',
      fontWeight: 'bold',
      cursor: 'pointer',
    });
    nextBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.nextStep();
    });
    nextBtn.addEventListener('click', () => this.nextStep());
    navBar.appendChild(nextBtn);

    this.overlay.appendChild(navBar);

    // Step indicator dots
    const dots = document.createElement('div');
    dots.id = 'tutorial-dots';
    Object.assign(dots.style, {
      position: 'absolute',
      bottom: '70px',
      left: '0',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
    });
    for (const i of this.getVisibleStepIndices()) {
      const dot = document.createElement('div');
      dot.dataset.step = String(i);
      Object.assign(dot.style, {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.3)',
        transition: 'background 0.2s',
      });
      dots.appendChild(dot);
    }
    this.overlay.appendChild(dots);

    if (isMobile()) {
      const dismissLabel = document.createElement('label');
      Object.assign(dismissLabel.style, {
        position: 'absolute',
        bottom: '108px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        background: 'rgba(0,0,0,0.35)',
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: '4px',
        color: 'rgba(255,255,255,0.8)',
        fontFamily: 'sans-serif',
        fontSize: '13px',
        zIndex: '3001',
      });
      this.dontShowAgainInput = document.createElement('input');
      this.dontShowAgainInput.type = 'checkbox';
      this.dontShowAgainInput.addEventListener('touchstart', (e) => {
        e.stopPropagation();
      }, { passive: true });
      dismissLabel.appendChild(this.dontShowAgainInput);
      const dismissText = document.createElement('span');
      dismissText.textContent = "Don't show again";
      dismissLabel.appendChild(dismissText);
      this.overlay.appendChild(dismissLabel);
    }

    document.body.appendChild(this.overlay);
  }

  /** Render the current step content (title, description, spotlight). */
  private renderStep(): void {
    if (!this.contentEl || !this.overlay) return;

    const step = TUTORIAL_STEPS[this.currentStepIndex];
    if (!step || !this.isStepAvailable(step)) return;
    const visibleStepIndices = this.getVisibleStepIndices();
    const visibleStepNumber = Math.max(1, visibleStepIndices.indexOf(this.currentStepIndex) + 1);

    // Update content
    this.contentEl.innerHTML = '';

    // Spotlight circle indicator
    // Prefer the live DOM rect of the target HUD button so the spotlight
    // tracks user-customized button positions from HudLayoutEditor. Fall
    // back to the hardcoded percentage coords when the element isn't
    // mounted (e.g., desktop, mode mismatch, or before HUD init).
    const liveRect = this.resolveSpotlightRect(step);
    const spotlight = document.createElement('div');
    if (liveRect) {
      Object.assign(spotlight.style, {
        position: 'absolute',
        left: `${liveRect.cx}px`,
        top: `${liveRect.cy}px`,
        width: `${liveRect.radius * 2}px`,
        height: `${liveRect.radius * 2}px`,
        marginLeft: `${-liveRect.radius}px`,
        marginTop: `${-liveRect.radius}px`,
        borderRadius: '50%',
        border: '3px solid rgba(0,180,255,0.6)',
        boxShadow: '0 0 30px rgba(0,180,255,0.3), inset 0 0 30px rgba(0,180,255,0.1)',
        pointerEvents: 'none',
        animation: 'tutorial-pulse 2s infinite',
      });
    } else {
      Object.assign(spotlight.style, {
        position: 'absolute',
        left: `${step.spotlightArea.x}%`,
        top: `${step.spotlightArea.y}%`,
        width: `${step.spotlightArea.radius * 2}px`,
        height: `${step.spotlightArea.radius * 2}px`,
        marginLeft: `${-step.spotlightArea.radius}px`,
        marginTop: `${-step.spotlightArea.radius}px`,
        borderRadius: '50%',
        border: '3px solid rgba(0,180,255,0.6)',
        boxShadow: '0 0 30px rgba(0,180,255,0.3), inset 0 0 30px rgba(0,180,255,0.1)',
        pointerEvents: 'none',
        animation: 'tutorial-pulse 2s infinite',
      });
    }

    // Inject pulse animation if not already injected
    if (!document.getElementById?.('tutorial-pulse-style')) {
      const style = document.createElement('style');
      style.id = 'tutorial-pulse-style';
      style.textContent = `
        @keyframes tutorial-pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.08); opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }

    // Step counter
    const counter = document.createElement('div');
    Object.assign(counter.style, {
      fontSize: '12px',
      color: 'rgba(255,255,255,0.5)',
      marginBottom: '8px',
    });
    counter.textContent = `Step ${visibleStepNumber} of ${visibleStepIndices.length}`;

    // Title
    const title = document.createElement('h2');
    Object.assign(title.style, {
      fontSize: '28px',
      fontWeight: 'bold',
      margin: '0 0 12px 0',
      letterSpacing: '2px',
      textTransform: 'uppercase',
    });
    title.textContent = step.title;

    // Description
    const desc = document.createElement('p');
    Object.assign(desc.style, {
      fontSize: '16px',
      lineHeight: '1.5',
      color: 'rgba(255,255,255,0.8)',
      margin: '0',
    });
    desc.textContent = step.description;

    this.contentEl.appendChild(counter);
    this.contentEl.appendChild(title);
    this.contentEl.appendChild(desc);

    // Add spotlight to overlay (remove previous spotlight)
    const prevSpotlight = this.overlay.querySelector?.('[data-spotlight]');
    if (prevSpotlight) prevSpotlight.remove();
    spotlight.dataset.spotlight = 'true';
    this.overlay.appendChild(spotlight);

    // Update step dots
    const dots = this.overlay.querySelector?.('#tutorial-dots');
    if (dots) {
      for (const dot of Array.from(dots.children ?? [])) {
        const dotEl = dot as HTMLElement;
        const stepIdx = parseInt(dotEl.dataset.step ?? '-1', 10);
        dotEl.style.background = stepIdx === this.currentStepIndex
          ? 'rgba(0,180,255,0.9)'
          : 'rgba(255,255,255,0.3)';
      }
    }
  }
}
